/**
 * Vuex store module for Try-On Preview & Testing
 */
export const state = () => ({
  defaultPrompt: null,
  result: null,
  loading: false,
})

export const mutations = {
  SET_DEFAULT_PROMPT(state, prompt) {
    state.defaultPrompt = prompt
  },
  SET_RESULT(state, result) {
    state.result = result
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  CLEAR_RESULT(state) {
    state.result = null
  },
}

export const actions = {
  /**
   * Fetch default try-on prompt
   * @returns {Promise<Object>} Prompt data
   */
  async fetchDefaultPrompt({ commit }) {
    try {
      const response = await this.$axios.$get('admin/aiPrompts', {
        params: { key: 'try_on_generate' },
      })
      // API returns { data: { id, key, prompt, ... } } when filtered by key
      const prompt = response.data || null
      commit('SET_DEFAULT_PROMPT', prompt)
      return prompt
    } catch (e) {
      console.error('Failed to fetch default prompt:', e)
      return null
    }
  },

  /**
   * Generate try-on preview
   * @param {Object} context - Vuex context
   * @param {Object} payload - Form data with images and settings
   * @returns {Promise<Object>} Preview result
   */
  async generatePreview({ commit }, payload) {
    commit('SET_LOADING', true)
    commit('CLEAR_RESULT')

    const formData = new FormData()

    // Required: user image
    formData.append('user_image', payload.userImage)

    // Product image - either from product or custom upload
    if (payload.productId && payload.mediaId) {
      formData.append('product_id', payload.productId)
      formData.append('media_id', payload.mediaId)
    } else if (payload.productImage) {
      formData.append('product_image', payload.productImage)
    }

    // Optional: prompt override
    if (payload.prompt) {
      formData.append('prompt', payload.prompt)
    }

    // Optional: temperature
    if (payload.temperature !== undefined) {
      formData.append('temperature', payload.temperature)
    }

    // Optional: skip validation
    if (payload.skipValidation) {
      formData.append('skip_validation', '1')
    }

    try {
      // Note: Do NOT set Content-Type header manually for FormData
      // Axios will automatically set the correct multipart/form-data with boundary
      const response = await this.$axios.$post('admin/tryOn/preview', formData)
      commit('SET_RESULT', response)
      return response
    } catch (e) {
      const errorResult = {
        success: false,
        message: e.response?.data?.message || 'Generation failed',
        data: e.response?.data?.data || {},
      }
      commit('SET_RESULT', errorResult)
      throw e
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * Save prompt as default
   * @param {Object} context - Vuex context
   * @param {Object} payload - { prompt, temperature }
   * @returns {Promise<Object>} Saved prompt data
   */
  async savePrompt(_, { prompt, temperature }) {
    const response = await this.$axios.$post('admin/tryOn/preview/save-prompt', {
      prompt,
      temperature,
    })
    return response
  },
}
